class LoggerService {
  constructor() {
    this.config = {
      // URL direta (produção) ou proxy local (desenvolvimento)
      // IMPORTANTE: Usar caminho absoluto com '/' no início para evitar concatenação com URL atual
      grafanaUrl: "/loki/api/v1/push",
      apiKey: import.meta.env.VITE_GRAFANA_API_KEY || "",
      userId: import.meta.env.VITE_GRAFANA_USER_ID || "",
      appName: import.meta.env.VITE_APP_NAME || "MENUUS-PROD",
      serviceName: import.meta.env.VITE_SERVICE_NAME || "MENUUS-FRONTEND-PROD",
      enabled: import.meta.env.VITE_LOGGING_ENABLED !== "false",
      consoleBackup: import.meta.env.VITE_CONSOLE_BACKUP !== "false",
      useBackendProxy: import.meta.env.VITE_USE_BACKEND_PROXY === "true",
      dev: import.meta.env.DEV === "true",
    };

    this.isConfigValid = this.validateConfig();
    this.queue = [];
    this.isProcessing = false;
  }

  // Determina a URL correta baseada no ambiente
  getGrafanaUrl() {
    // Em desenvolvimento, não envia para Grafana (apenas console)
    if (import.meta.env.DEV) {
      return "";
    }

    // Em produção, usa a URL direta ou proxy do backend
    return "http://loki.menuus.com.br/loki/api/v1/push";
  }

  // Função para sanitizar chaves de labels do Loki
  sanitizeLabelKey(key) {
    if (!key || typeof key !== "string") {
      return null;
    }

    // Remove caracteres inválidos e garante que comece com letra ou underscore
    let sanitized = key
      .replace(/[^a-zA-Z0-9_]/g, "_") // Substitui caracteres especiais por underscore
      .replace(/^[0-9]+/, "n_"); // Se começar com número, adiciona 'n_' no início

    // Se ficou vazio ou só underscores, retorna null
    if (!sanitized || /^_+$/.test(sanitized)) {
      return null;
    }

    // Garante que comece com letra ou underscore
    if (!/^[a-zA-Z_]/.test(sanitized)) {
      sanitized = "label_" + sanitized;
    }

    return sanitized;
  }

  // Função para sanitizar dados antes de enviar para Loki
  sanitizeForLoki(obj) {
    if (obj === null || obj === undefined) {
      return {};
    }

    // Se for uma string ou número, retorna como está
    if (
      typeof obj === "string" ||
      typeof obj === "number" ||
      typeof obj === "boolean"
    ) {
      return obj;
    }

    // Se for um array, pode causar problemas no Loki
    if (Array.isArray(obj)) {
      return `[Array com ${obj.length} itens]`;
    }

    // Se for objeto, filtra apenas propriedades seguras
    if (typeof obj === "object") {
      const sanitized = {};
      Object.keys(obj).forEach((key) => {
        const value = obj[key];

        // Sanitiza a chave para ser válida como label do Loki
        const sanitizedKey = this.sanitizeLabelKey(key);

        if (
          sanitizedKey &&
          (typeof value === "string" ||
            typeof value === "number" ||
            typeof value === "boolean")
        ) {
          // Limita tamanho para evitar overflow
          sanitized[sanitizedKey] = String(value).substring(0, 200);
        }
      });
      return sanitized;
    }

    return String(obj).substring(0, 200);
  }

  validateConfig() {
    if (import.meta.env.DEV == false) {
      console.info(
        "[Logger] 🔧 Modo desenvolvimento: logs apenas no console, não enviados para Grafana",
      );
      return false; // Desabilita envio para Grafana
    }

    if (!this.config.grafanaUrl) {
      console.warn("[Logger] URL do Grafana não configurada");
      return false;
    }

    // Se não estiver usando proxy, precisa das credenciais
    if (!this.config.useBackendProxy) {
      if (!this.config.apiKey) {
        console.warn("[Logger] VITE_GRAFANA_API_KEY não configurado");
        return false;
      }
      if (!this.config.userId) {
        console.warn(
          "[Logger] VITE_GRAFANA_USER_ID não configurado (necessário para Basic Auth)",
        );
        return false;
      }
    }

    return true;
  }

  formatMessage(level, message, context = {}) {
    const timestamp = new Date().toISOString();

    // Sanitiza a mensagem
    let cleanMessage = message;
    if (typeof message === "object") {
      cleanMessage = JSON.stringify(message);
    } else if (typeof message === "string") {
      cleanMessage = message;
    } else {
      cleanMessage = String(message);
    }

    // Sanitiza o contexto para o stream usando função dedicada
    const streamLabels = this.sanitizeForLoki(context);

    const logEntry = {
      timestamp,
      level,
      message: cleanMessage,
      app: this.config.appName,
      service: this.config.serviceName,
      userAgent: navigator.userAgent,
      url: window.location.href,
      ...context, // Contexto completo vai apenas no log entry
    };

    return {
      stream: {
        level,
        app: this.config.appName,
        service: this.config.serviceName,
        // Apenas adiciona labels válidas e seguras
        ...(Object.keys(streamLabels).length > 0 ? streamLabels : {}),
      },
      values: [[Date.now() + "000000", JSON.stringify(logEntry)]],
    };
  }

  async sendToLoki(streams) {
    if (!this.isConfigValid || !this.config.enabled) {
      return;
    }

    try {
      const headers = {
        "Content-Type": "application/json",
        "x-api-key": "7ef24879-8058-43ef-a600-4e13284cf772",
      };

      // Se usar proxy do backend, não precisa do Authorization header (proxy adiciona)
      const usingProxy = this.config.useBackendProxy;

      if (!usingProxy && this.config.apiKey) {
        // Grafana Cloud Loki usa Basic Auth: username = User ID, password = API Token
        const userId = import.meta.env.VITE_GRAFANA_USER_ID || "";
        if (userId) {
          const basicAuth = btoa(`${userId}:${this.config.apiKey}`);
          headers["Authorization"] = `Basic ${basicAuth}`;
        } else {
          console.warn(
            "[Logger] VITE_GRAFANA_USER_ID não configurado. Necessário para Basic Auth.",
          );
        }
      }

      const response = await fetch(this.config.grafanaUrl, {
        method: "POST",
        headers,
        body: JSON.stringify({ streams }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      // Log de sucesso apenas em desenvolvimento
      if (import.meta.env.DEV && this.config.consoleBackup) {
        console.log(
          `[Logger] ✅ Logs enviados com sucesso (${streams.length} streams)`,
        );
      }
    } catch (error) {
      if (this.config.consoleBackup) {
        console.error(`[Logger] ❌ Falha ao enviar para Loki:`, error.message);

        // Em desenvolvimento, mostra mais detalhes
        if (import.meta.env.DEV) {
          console.group("[Logger] Detalhes do erro:");

          console.groupEnd();
        }
      }
    }
  }

  async processQueue() {
    if (this.isProcessing || this.queue.length === 0) return;

    this.isProcessing = true;
    const streams = [...this.queue];
    this.queue = [];

    await this.sendToLoki(streams);
    this.isProcessing = false;
  }

  log(level, message, context = {}) {
    // Backup no console se habilitado
    if (this.config.consoleBackup) {
      const consoleMethod = console[level] || console.log;
      consoleMethod(`[${level.toUpperCase()}]`, message, context);
    }

    if (!this.config.enabled) return;

    const logStream = this.formatMessage(level, message, context);
    this.queue.push(logStream);

    // Process queue with debounce
    setTimeout(() => this.processQueue(), 100);
  }
}

// Instância singleton do logger
const loggerInstance = new LoggerService();

// Interface pública do logger
export const logger = {
  info: (message, context) => loggerInstance.log("info", message, context),
  warn: (message, context) => loggerInstance.log("warn", message, context),
  error: (message, context) => loggerInstance.log("error", message, context),
  debug: (message, context) => loggerInstance.log("debug", message, context),

  // Métodos utilitários
  logUserAction: (action, details) =>
    loggerInstance.log("info", `User Action: ${action}`, {
      type: "user_action",
      action,
      ...details,
    }),

  logPerformance: (metric, value, context) =>
    loggerInstance.log("info", `Performance: ${metric}`, {
      type: "performance",
      metric,
      value,
      ...context,
    }),

  logBusinessEvent: (event, data) =>
    loggerInstance.log("info", `Business Event: ${event}`, {
      type: "business_event",
      event,
      ...data,
    }),

  // Método para substituir console.log existentes
  console: (message, ...args) => {
    const formattedMessage =
      args.length > 0 ? `${message} ${args.join(" ")}` : message;
    loggerInstance.log("info", formattedMessage, { type: "console_migration" });
  },
};

export default {
  install: (app) => {
    app.config.globalProperties.$log = logger;
    app.provide("log", logger);
  },
};
